export type ProductStatus = "available" | "coming-soon";
export type ProductPlan = {
  name: string;
  price: string;
  note: string;
  features: string[];
  featured?: boolean;
};
export type TechnicalComparisonRow = { label: string; values: string[] };
export type ComparisonGuide = { href: string; label: string };

export type ProductContentBlock = {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  tone: "cloud" | "cream" | "ink";
  items: {
    title: string;
    description: string;
    icon: "app" | "database" | "git" | "container" | "domain" | "backup";
  }[];
};

export type Product = {
  slug: string;
  name: string;
  description: string;
  updatedAt?: string;
  status: ProductStatus;
  href: string;
  startingAt?: string;
  featuredOnHome?: boolean;
  features?: string[];
  plans?: ProductPlan[];
  technicalRows?: TechnicalComparisonRow[];
  comparisonGuide?: ComparisonGuide;
  contentBlocks?: ProductContentBlock[];
  supportedApps?: { name: string; description: string }[];
};

export type ProductCategory = {
  id: string;
  slug: string;
  name: string;
  navLabel: string;
  order: number;
  showInMenu: boolean;
  description: string;
  updatedAt?: string;
  products: Product[];
};

export const sharedHostingPlans: ProductPlan[] = [
  {
    name: "Starter",
    price: "$2.99",
    note: "For one app, website, portfolio, store, or personal project.",
    features: [
      "1 website / app",
      "1 database",
      "5 email addresses",
      "1 GB storage",
    ],
  },
  {
    name: "Growth",
    price: "$8.99",
    note: "For multiple projects, client work, or growing applications.",
    features: [
      "5 websites / apps",
      "5 databases",
      "10 email addresses",
      "5 GB storage",
    ],
    featured: true,
  },
  {
    name: "Business",
    price: "$15.99",
    note: "For agencies, teams, and larger sets of hosted projects.",
    features: [
      "10 websites / apps",
      "10 databases",
      "15 email addresses",
      "10 GB storage",
    ],
  },
];

const sharedHosting: Product = {
  slug: "shared-hosting",
  name: "Shared hosting",
  description:
    "A straightforward home for websites, applications, online shops, and WordPress.",
  status: "available",
  href: "/products/web-hosting/shared-hosting",
  startingAt: "Plans from $2.99/month",
  featuredOnHome: true,
  features: [
    "Free SSL",
    "Automatic backups",
    "Migration guidance",
    "Python, Node.js, and PHP support",
  ],
  technicalRows: [
    { label: "Websites / apps", values: ["1", "5", "10"] },
    { label: "Databases", values: ["1", "5", "10"] },
    { label: "Email accounts", values: ["5", "10", "15"] },
    { label: "Storage", values: ["1 GB", "5 GB", "10 GB"] },
    { label: "CPU", values: ["Up to 1 core", "Up to 1 core", "Up to 1 core"] },
    { label: "RAM", values: ["Up to 2 GB", "Up to 2 GB", "Up to 2 GB"] },
    { label: "Backup cadence", values: ["Daily", "Daily", "Daily"] },
    { label: "SSL certificate", values: ["Included", "Included", "Included"] },
    { label: "DNS management", values: ["Included", "Included", "Included"] },
    { label: "Cron jobs", values: ["Included", "Included", "Included"] },
    { label: "Git access", values: ["Included", "Included", "Included"] },
    { label: "SSH access", values: ["Included", "Included", "Included"] },
    { label: "Redis", values: ["Included", "Included", "Included"] },
    {
      label: "Python app setup",
      values: ["Included", "Included", "Included"],
    },
    {
      label: "Node.js app setup",
      values: ["Included", "Included", "Included"],
    },
    {
      label: "PHP version control",
      values: ["Included", "Included", "Included"],
    },
    {
      label: "One-click installer",
      values: ["Included", "Included", "Included"],
    },
  ],
  contentBlocks: [
    {
      id: "shared-basics",
      eyebrow: "Everything around your project",
      title: "Your hosting stack, already connected.",
      description:
        "HostRight combines the things most projects need in one account: domains, databases, professional email, SSL, DNS, backups, cron jobs, Redis, and deployment-friendly access.",
      tone: "cloud",
      items: [
        {
          title: "Databases",
          description:
            "Keep application data close to the projects that use it.",
          icon: "database",
        },
        {
          title: "Professional email",
          description: "Create inboxes alongside your websites and apps.",
          icon: "app",
        },
        {
          title: "SSL included",
          description: "Keep every visitor connection protected by default.",
          icon: "domain",
        },
        {
          title: "DNS management",
          description:
            "Point domains and subdomains where your project needs them.",
          icon: "backup",
        },
        {
          title: "Backups",
          description: "Keep a recoverable copy of the work you have shipped.",
          icon: "backup",
        },
        {
          title: "Cron jobs + Redis",
          description:
            "Schedule work and add practical caching where it helps.",
          icon: "container",
        },
      ],
    },
  ],
};

export const productCategories: ProductCategory[] = [
  {
    id: "hosting",
    slug: "web-hosting",
    name: "Web hosting",
    navLabel: "Web hosting",
    order: 1,
    showInMenu: true,
    description:
      "Reliable shared hosting for websites, stores, and portfolios.",
    products: [sharedHosting],
  },
];

export function getProductCategory(slug: string) {
  return productCategories.find((category) => category.slug === slug);
}

export function getProduct(categorySlug: string, productSlug: string) {
  return getProductCategory(categorySlug)?.products.find(
    (product) => product.slug === productSlug,
  );
}
